import React from 'react';
import { AlertBanner, SparkIcon, Text } from '@spark';
import ClassificationSummary from './ClassificationSummary';
import NerSummary from './NerSummary';
import { TaskType, isGenerativeTask, getGenerativeOutputNoun } from '@/shared/domain/taskTypes';

const ValidationAlert = ({ validationResult, taskType, isMultilabel }) => {

    const renderParserOutput = () => {
        if (!validationResult.response_parser) return null;

        const parserOutput = validationResult.response_parser;

        if (taskType === TaskType.classification) {
            return (
                <ClassificationSummary
                    taskType={taskType}
                    isMultilabel={isMultilabel}
                    parserOutput={parserOutput}
                />
            );
        }

        if (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) {
            return (
                <NerSummary
                    isNerJsonMode={true}
                    supportsRelationFields={taskType === TaskType.NER_WITH_RELATIONS}
                    parserOutput={parserOutput}
                />
            );
        }

        // Generative tasks: show only the extracted summary/response, not the
        // full response object. Mirror the classification "Extracted Labels:"
        // card styling.
        if (isGenerativeTask(taskType)) {
            const summary = parserOutput?.response ?? '';
            const noun = getGenerativeOutputNoun(taskType);
            return (
                <div
                    className="response-step-card__summarization-summary"
                    style={{
                        marginTop: 16,
                        border: '1px solid #e5e5e5',
                        borderRadius: 12,
                        padding: 16,
                        background: '#fff',
                    }}
                >
                    <Text variant="base-regular" color="secondary">Extracted {noun}:</Text>
                    <div style={{ marginTop: 8 }}>
                        {summary
                            ? <Text variant="base-regular" style={{ whiteSpace: 'pre-wrap' }}>{summary}</Text>
                            : <Text variant="base-regular" color="secondary">No {noun} found</Text>}
                    </div>
                </div>
            );
        }

        return (
            <Text
                variant="base-regular"
                style={{
                    fontFamily: 'monospace',
                    background: 'rgba(150, 150, 150, 0.1)',
                    border: '1px solid rgba(100, 100, 100, 0.2)',
                    borderRadius: 3,
                    padding: '2px 4px',
                }}
            >
                {JSON.stringify(parserOutput, null, 2)}
            </Text>
        );
    };

    // Handle success case
    if (validationResult.is_valid) {
        return (
            <div style={{}}>
                {validationResult.response_parser && (
                    <div>
                        <div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <span style={{ color: '#52c41a', display: 'inline-flex' }}>
                                    <SparkIcon slug="circle-check" size="small" />
                                </span>
                                <Text variant="base-regular">Validation Successful</Text>
                            </div>
                        </div>
                        {renderParserOutput()}
                    </div>
                )}
            </div>
        );
    }

    // Handle error case
    const formatErrorMessage = (message) => {
        if (!message) return [];
        const lines = message.split('\n');
        const errors = [];

        let currentError = null;
        lines.forEach(line => {
            if (line.trim() && !line.includes('https://')) {
                if (line.includes('Input should')) {
                    if (currentError) errors.push(currentError);
                    currentError = line.trim();
                } else if (currentError) {
                    currentError += ' ' + line.trim();
                } else {
                    currentError = line.trim();
                }
            }
        });
        if (currentError) errors.push(currentError);
        return errors;
    };

    const errorMessages = formatErrorMessage(validationResult.error_message);

    return (
        <div style={{}}>
            <AlertBanner
                feedback="error"
                showButton={false}
                showCloseButton={false}
                text={
                    <div>
                        <div>Validation Error</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                            {errorMessages.map((error, index) => (
                                <Text variant="base-regular" key={index}>• {error}</Text>
                            ))}

                            {/* Results Section */}

                            {validationResult.suggestion && (
                                <Text variant="base-semibold" style={{ whiteSpace: "pre-wrap" }}>
                                    Suggestion: {validationResult.suggestion}
                                </Text>
                            )}

                            {validationResult.response_parser && (
                                <div>
                                    <Text variant="heading-5-semibold" as="h5">response_parser output:</Text>
                                    {renderParserOutput()}
                                </div>
                            )}
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default ValidationAlert;
